import { AdPrivacyOptionsButton } from '@gagovictor/shared-frontend-core/dist/ads';
import { Box, Link, Paper, Typography } from '@mui/material';
import React from 'react';

import { MessageKey, useI18n } from '../i18n';
import { adConsent } from '../monetization/admob';
import { config } from '../monetization/config';

import { PageShell } from './components/PageShell';

/** Bump when the policy text changes. */
const LAST_UPDATED = new Date(2026, 9, 8);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
}) => (
    <Box sx={{ mt: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
            {title}
        </Typography>
        {children}
    </Box>
);

/** The privacy policy, in the language of the app. It describes what Arcana actually does: no accounts, no server. */
export const PrivacyPage: React.FC = () => {
    const { t, locale } = useI18n();
    const text = (key: MessageKey) => (
        <Typography paragraph>{t(key)}</Typography>
    );
    return (
        <PageShell to="/">
            <Paper
                component="article"
                sx={{ p: { xs: 2.5, sm: 4 }, background: 'rgba(21,14,36,.88)' }}
            >
                <Typography
                    variant="h4"
                    component="h1"
                    gutterBottom
                    data-testid="privacy-policy-title"
                >
                    {t('privacy.title')}
                </Typography>
                {text('privacy.intro')}
                <Section title={t('privacy.deviceTitle')}>
                    {text('privacy.p1')}
                    {text('privacy.journal')}
                </Section>
                <Section title={t('privacy.adsTitle')}>
                    <Typography paragraph>
                        {t('privacy.p2a')}{' '}
                        <Link
                            href="https://adssettings.google.com"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t('privacy.googleSettings')}
                        </Link>{' '}
                        {t('privacy.p2b')}{' '}
                        <Link
                            href="https://policies.google.com/technologies/partner-sites"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            policies.google.com/technologies/partner-sites
                        </Link>
                        .
                    </Typography>
                    {text('privacy.p3')}
                    {text('privacy.consent')}
                    <AdPrivacyOptionsButton
                        consent={adConsent}
                        label={t('privacy.adChoices')}
                    />
                </Section>
                <Section title={t('privacy.ageTitle')}>
                    {text('privacy.age')}
                </Section>
                <Section title={t('privacy.changesTitle')}>
                    {text('privacy.changes')}
                </Section>
                <Section title={t('privacy.contactTitle')}>
                    <Typography paragraph>
                        {t('privacy.contact')}{' '}
                        <Link href={`mailto:${config.supportEmail}`}>
                            {config.supportEmail}
                        </Link>
                        .
                    </Typography>
                </Section>
                <Typography variant="caption" color="text.secondary">
                    {t('privacy.updated', {
                        date: LAST_UPDATED.toLocaleDateString(locale),
                    })}
                </Typography>
            </Paper>
        </PageShell>
    );
};
